import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Grid,
  Avatar,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  alpha
} from '@mui/material';
//import { useThemeMode } from '../contexts/ThemeContext';
import { auth } from '../firebase';
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import SaveIcon from '@mui/icons-material/Save';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SecurityIcon from '@mui/icons-material/Security';
//import PaletteIcon from '@mui/icons-material/Palette';
//import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import apiService, { SubscriptionResponse } from '../services/api';

const SettingsPage: React.FC = () => {
  const theme = useTheme();
  //const { mode, toggleTheme } = useThemeMode();
  const [saved, setSaved] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [savedSeverity, setSavedSeverity] = useState<'success' | 'error'>('success');
  
  // User data
  const [userEmail, setUserEmail] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
  const [memberSince, setMemberSince] = useState<string>('');
  const [loading, setLoading] = useState(true);
  
  // Password change dialog
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState({
    emailNotifications: true,
    autoSave: true,
    compactView: false
  });

  // Load user data
  useEffect(() => {
    const loadUserData = async () => {
      setLoading(true);
      try {
        const user = auth.currentUser;
        if (user) {
          setUserEmail(user.email || '');
          setUserName(user.displayName || user.email?.split('@')[0] || 'User');
          
          // Load subscription data
          const sub = await apiService.getSubscription();
          setSubscription(sub);
          
          // Get member since from Firebase user metadata
          if (user.metadata.creationTime) {
            const created = new Date(user.metadata.creationTime);
            setMemberSince(created.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' }));
          }
          
          // Load saved preferences from localStorage
          const savedPrefs = localStorage.getItem('userPreferences');
          if (savedPrefs) {
            const prefs = JSON.parse(savedPrefs);
            setSettings(prev => ({ ...prev, ...prefs }));
          }
        }
      } catch (error) {
        console.error('Failed to load user data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadUserData();
  }, []);

  // Save preferences to localStorage
  const handleSave = () => {
    localStorage.setItem('userPreferences', JSON.stringify(settings));
    setSavedMessage('Settings saved successfully!');
    setSavedSeverity('success');
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  // Change password with Firebase
  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setSavedMessage('New passwords do not match');
      setSavedSeverity('error');
      setSaved(true);
      return;
    }
    
    if (newPassword.length < 6) {
      setSavedMessage('Password must be at least 6 characters');
      setSavedSeverity('error');
      setSaved(true);
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new Error('User not authenticated');
      }
      
      // Re-authenticate user before changing password
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      // Update password
      await updatePassword(user, newPassword);
      
      setSavedMessage('Password changed successfully!');
      setSavedSeverity('success');
      setSaved(true);
      setPasswordDialogOpen(false);
      
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
    } catch (error: any) {
      console.error('Password change error:', error);
      if (error.code === 'auth/wrong-password') {
        setSavedMessage('Current password is incorrect');
      } else if (error.code === 'auth/requires-recent-login') {
        setSavedMessage('Please log in again to change your password');
      } else {
        setSavedMessage(error.message || 'Failed to change password');
      }
      setSavedSeverity('error');
      setSaved(true);
    } finally {
      setPasswordLoading(false);
    }
  };

  // Get plan name from subscription
  const getPlanName = () => {
    if (!subscription) return 'Free';
    const plan = subscription.plan;
    switch (plan) {
      case 501900000: return 'Starter';
      case 501900001: return 'Business';
      case 501900002: return 'Enterprise';
      case 501900003: return 'Free Trial';
      default: return 'Unknown';
    }
  };

  // Get first letter for avatar
  const getInitials = () => {
    return userName.charAt(0).toUpperCase();
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          fontWeight={800}
          sx={{
            background: "linear-gradient(135deg, #0A2540 0%, #1E3A5F 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 1
          }}
        >
          Settings
        </Typography>
        <Typography variant="body1" sx={{ color: '#5B6F82' }}>
          Manage your account preferences and application settings
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Profile Settings */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: '#FDBB30',
                  color: '#0A1E3C',
                  fontSize: '2rem',
                  fontWeight: 700
                }}
              >
                {getInitials()}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700}>{userName}</Typography>
                <Typography variant="body2" color="text.secondary">{userEmail}</Typography>
              </Box>
            </Box>
            


            <Divider sx={{ my: 3 }} />

            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Account Status
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Plan</Typography>
                <Typography variant="body2" fontWeight={600}>{getPlanName()}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Member since</Typography>
                <Typography variant="body2">{memberSince || 'N/A'}</Typography>
              </Box>
              {subscription?.period === 501900001 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2">Billing period</Typography>
                  <Typography variant="body2" fontWeight={600}>Yearly</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Main Settings */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>


            <Divider sx={{ my: 3 }} />

            {/* Notifications */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <NotificationsIcon sx={{ color: '#FDBB30' }} />
                <Typography variant="h6" fontWeight={700}>Notifications</Typography>
              </Box>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.emailNotifications}
                    onChange={(e) => setSettings({ ...settings, emailNotifications: e.target.checked })}
                  />
                }
                label="Email notifications for important updates"
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Security */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SecurityIcon sx={{ color: '#FDBB30' }} />
                <Typography variant="h6" fontWeight={700}>Security</Typography>
              </Box>
              
              <Button 
                variant="outlined" 
                size="medium" 
                onClick={() => setPasswordDialogOpen(true)}
                sx={{
                  borderColor: theme.palette.secondary.main,
                  color: theme.palette.secondary.main,
                  '&:hover': {
                    borderColor: theme.palette.secondary.dark,
                    backgroundColor: alpha(theme.palette.secondary.main, 0.05)
                  }
                }}
              >
                Change Password
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Save Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                sx={{
                  background: "linear-gradient(135deg, #FDBB30 0%, #FFD966 100%)",
                  color: "#0A1E3C",
                  '&:hover': {
                    background: "linear-gradient(135deg, #FFD966 0%, #FDBB30 100%)"
                  }
                }}
              >
                Save Preferences
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Password Change Dialog */}
      <Dialog 
        open={passwordDialogOpen} 
        onClose={() => setPasswordDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: { xs: '90%', sm: 400 }
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>Change Password</Typography>
          <Typography variant="caption" color="text.secondary">
            Enter your current password and choose a new one
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Current Password"
            type="password"
            fullWidth
            variant="outlined"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="New Password"
            type="password"
            fullWidth
            variant="outlined"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText="Password must be at least 6 characters"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Confirm New Password"
            type="password"
            fullWidth
            variant="outlined"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={confirmPassword !== '' && newPassword !== confirmPassword}
            helperText={confirmPassword !== '' && newPassword !== confirmPassword ? "Passwords don't match" : ""}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button 
            onClick={() => setPasswordDialogOpen(false)}
            sx={{ color: '#5B6F82' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleChangePassword}
            disabled={passwordLoading || !currentPassword || !newPassword || newPassword !== confirmPassword}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.light} 100%)`,
              color: '#0A2540',
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`
              }
            }}
          >
            {passwordLoading ? <CircularProgress size={24} /> : 'Update Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for messages */}
      <Snackbar
        open={saved}
        autoHideDuration={4000}
        onClose={() => setSaved(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSaved(false)} 
          severity={savedSeverity} 
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {savedMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SettingsPage;